import { useEffect, useState } from 'react'
import { Alert, Button, List, Stack, Text, Textarea } from '@mantine/core'
import { IconCode } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { adotarNoCodigo, confirmarPedidoDeCodigo, type ModoCodigo, type PedidoNoCodigo } from '@/api/waf'
import { formatApiError } from '@/api/errors'
import { useAuthStore, useCan } from '@/store/auth'
import { CHAVE_PEDIDO_TERRAFORM, useTerraformChangeRequest } from '@/hooks/useTerraformChangeRequest'
import { PainelPedidoNoCodigo } from './PainelPedidoNoCodigo'

/** Os motivos do 422 `adocao_recusada`: cada um é algo que o stage ou o módulo
 * precisa resolver antes, e a tela lista todos. */
function motivosDaRecusa(err: unknown): string[] | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  const motivos = (detail as { erro?: string; motivos?: unknown } | null)?.motivos
  return (detail as { erro?: string } | null)?.erro === 'adocao_recusada' && Array.isArray(motivos)
    ? motivos.map(String) : null
}

/**
 * WAF de tenant TF+GIT cujo stage ainda não tem `module "waf"` (spec 2026-10-15
 * WAF 100% D10.4): o botão abre o pedido que escreve o `waf.tf` com os `import`
 * da ACL existente. Quem decide se a adoção é segura é o plan — o portão recusa
 * adoção que destrói ou afrouxa —, e o pedido segue o mesmo caminho dos gestos
 * do módulo (plan, aprovação do DevOps Sênior, MR, apply).
 */
export function PainelDeAdocao({ wafId, modo }: { wafId: number; modo: ModoCodigo }) {
  const qc = useQueryClient()
  const can = useCan()
  const { user } = useAuthStore()
  const recarregar = () => qc.invalidateQueries({ queryKey: ['waf-codigo', wafId] })
  const [justificativa, setJustificativa] = useState('')
  const [recemCriado, setRecemCriado] = useState<number | null>(null)

  const adotar = useMutation({
    mutationFn: () => adotarNoCodigo(wafId, justificativa.trim()),
    onSuccess: r => { setRecemCriado(r.pedido_id); recarregar() },
  })
  const confirmar = useMutation({
    mutationFn: (pedidoId: number) => confirmarPedidoDeCodigo(wafId, pedidoId),
    onSuccess: () => { recarregar(); qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM] }) },
  })

  const ativo = modo.pedido_ativo
  const parado = modo.pedido_parado ?? null
  const recemNaCarga = recemCriado != null && (ativo?.id === recemCriado || parado?.id === recemCriado)
  const recem: PedidoNoCodigo | null = recemCriado != null && !ativo && !recemNaCarga ? {
    id: recemCriado, estado: 'rascunho', origem: 'tela_waf', waf_acao: { tipo: 'adotar_no_codigo' },
    requested_by_user_id: user?.id ?? null, mr_iid: null, aprovado_no_plan: null, motivo: null,
    waf_reconciliado: [], resumo: null,
  } : null
  const ocupando = ativo ?? recem
  const pedido = ocupando ?? parado
  const acompanhamento = useTerraformChangeRequest(pedido?.id ?? null)
  const estadoAtual = acompanhamento.data?.estado
  useEffect(() => {
    if (pedido && estadoAtual && estadoAtual !== pedido.estado) recarregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoAtual])
  useEffect(() => {
    if (recemNaCarga) setRecemCriado(null)
  }, [recemNaCarga])

  const motivos = adotar.isError ? motivosDaRecusa(adotar.error) : null
  const pode = can('waf', 'rule:manage')
  return (
    <Stack gap="sm">
      {pedido && (
        <PainelPedidoNoCodigo
          pedido={pedido}
          parado={!ocupando}
          souRequerente={user?.id != null && user.id === pedido.requested_by_user_id}
          onConfirmar={() => confirmar.mutate(pedido.id)}
          confirmando={confirmar.isPending}
          erro={confirmar.isError ? formatApiError(confirmar.error, 'Erro ao confirmar.') : null}
          acompanhamento={acompanhamento.data ?? null}
          onDecidido={() => { recarregar(); qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM] }) }}
        />
      )}
      {!ocupando && (
        <Alert color="gray" icon={<IconCode size={16} />}
          title={`Este WAF ainda não é do código do stage ${modo.adocao?.stage_chave ?? ''}`}>
          <Stack gap="xs">
            <Text size="sm">
              O stage não tem o <code>module "waf"</code>. Trazer o WAF para o código escreve um
              arquivo <code>waf.tf</code> no stage com os valores lidos da AWS e os <code>import</code>{' '}
              que adotam esta ACL, sem criar outra. O plan decide: adoção que destrói ou deixa o WAF
              mais fraco é recusada.
            </Text>
            {pode && (
              <>
                <Textarea
                  label="Justificativa"
                  description="Vai no pedido e no MR."
                  value={justificativa}
                  onChange={e => setJustificativa(e.currentTarget.value)}
                  minRows={2}
                  maxLength={2000}
                />
                <Button
                  w="fit-content"
                  onClick={() => adotar.mutate()}
                  loading={adotar.isPending}
                  disabled={justificativa.trim().length < 10}
                >
                  Trazer este WAF para o código
                </Button>
              </>
            )}
            {motivos && (
              <Alert color="red" title="A adoção foi recusada">
                <List size="sm">{motivos.map(m => <List.Item key={m}>{m}</List.Item>)}</List>
              </Alert>
            )}
            {adotar.isError && !motivos && (
              <Text size="sm" c="red">{formatApiError(adotar.error, 'Erro ao pedir a adoção.')}</Text>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  )
}
